import {url} from '../../App/AuthenticatedRoute'
import {OrderCreateState, OrderCreateTab} from './OrderCreateState'
import {OrderCreateAction} from './OrderCreateActions'
import {
    OrderCreateFeedback,
    OrderCreateRequestIdentifier
} from './OrderCreateFeedback'
import {update} from '../../Data/ObjectHelper'
import * as Result from '../../Data/Result'
import * as OptionalWebData from '../../Data/OptionalWebData'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {initStep1, step1Reducer, validateStep1} from './Step1/Step1Reducer'
import {initStep2, step2Reducer, validateStep2} from './Step2/Step2Reducer'
import {initStep3, step3Reducer, validateStep3} from './Step3/Step3Reducer'
import {Session} from '../../Data/Session'
import {navigate} from '../../Data/Browser'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import * as OrderResource from '../../Request/OrderResource'
import {fromSteps} from '../../Entities/OrderCreateWithDetails'
import {string} from 'json-bouncer'

export function init(): OrderCreateLoop {
    const loop = initStep1(null)
    return {
        state: {
            currentTab: {
                type: 'Step1Tab',
                index: 1,
                state: loop.state,
                step1: null,
                step2: null,
                step3: null,
            },
            createdOrder: {type: 'NotAsked'},
        },
        feedback: loop.feedback,
    }
}

type OrderCreateLoop =
    FeedbackLoop<OrderCreateState, OrderCreateFeedback>

export function reduce(
    session: Session,
    state: OrderCreateState,
    action: OrderCreateAction
): OrderCreateLoop {
    switch (action.type) {
        case 'PreviousStep': {
            return (() => {
                switch (state.currentTab.type) {
                    case 'Step2Tab': {
                        const loop = initStep1(state.currentTab.step1)
                        return {
                            state: update(
                                {
                                    currentTab: {
                                        type: 'Step1Tab',
                                        index: 1,
                                        state: loop.state,
                                        step1: state.currentTab.step1,
                                        step2: state.currentTab.step2,
                                        step3: state.currentTab.step3,
                                    }
                                },
                                state,
                            ),
                            feedback: loop.feedback,
                        }
                    }
                    case 'Step3Tab': {
                        const loop = initStep2(state.currentTab.step2)
                        return {
                            state: update(
                                {
                                    currentTab: {
                                        type: 'Step2Tab',
                                        index: 2,
                                        state: loop.state,
                                        step1: state.currentTab.step1,
                                        step2: state.currentTab.step2,
                                        step3: state.currentTab.step3,
                                    }
                                },
                                state,
                            ),
                            feedback: loop.feedback,
                        }
                    }
                    case 'Step4Tab':
                        return {
                            state: update(
                                {
                                    currentTab: {
                                        type: 'Step3Tab',
                                        index: 3,
                                        state: initStep3(
                                            state.currentTab.step1.company,
                                            state.currentTab.step3,
                                        ),
                                        step1: state.currentTab.step1,
                                        step2: state.currentTab.step2,
                                        step3: state.currentTab.step3,
                                    }
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    default:
                        return {state, feedback: []}
                }
            })()
        }
        case 'NextStep': {
            return ((): OrderCreateLoop => {
                switch (state.currentTab.type) {
                    case 'Step1Tab': {
                        const outcome = validateStep1(state.currentTab.state)
                        if (outcome.data !== null) {
                            const loop = initStep2(state.currentTab.step2)
                            return {
                                state: update(
                                    {
                                        currentTab: {
                                            type: 'Step2Tab',
                                            index: 2,
                                            state: loop.state,
                                            step1: outcome.data,
                                            step2: state.currentTab.step2,
                                            step3: state.currentTab.step3,
                                        }
                                    },
                                    state,
                                ),
                                feedback: loop.feedback,
                            }
                        } else {
                            return {
                                state: update(
                                    {
                                        currentTab: update(
                                            {state: outcome.state},
                                            state.currentTab,
                                        ),
                                    },
                                    state,
                                ),
                                feedback: [],
                            }
                        }
                    }
                    case 'Step2Tab': {
                        const outcome = validateStep2(state.currentTab.state)
                        if (outcome.data !== null) {
                            return {
                                state: update(
                                    {
                                        currentTab: {
                                            type: 'Step3Tab',
                                            index: 3,
                                            state: initStep3(
                                                state.currentTab.step1.company,
                                                state.currentTab.step3,
                                            ),
                                            step1: state.currentTab.step1,
                                            step2: outcome.data,
                                            step3: state.currentTab.step3,
                                        }
                                    },
                                    state,
                                ),
                                feedback: [],
                            }
                        } else {
                            return {
                                state: update(
                                    {
                                        currentTab: update(
                                            {state: outcome.state},
                                            state.currentTab,
                                        ),
                                    },
                                    state,
                                ),
                                feedback: [],
                            }
                        }
                    }
                    case 'Step3Tab': {
                        const step3Data = validateStep3(state.currentTab.state)
                        return {
                            state: update(
                                {
                                    currentTab: {
                                        type: 'Step4Tab',
                                        index: 4,
                                        step1: state.currentTab.step1,
                                        step2: state.currentTab.step2,
                                        step3: step3Data,
                                    }
                                },
                                state,
                            ),
                            feedback: [],
                        }
                    }
                    case 'Step4Tab': {
                        return {
                            state: update(
                                {createdOrder: {type: 'Loading'}},
                                state,
                            ),
                            feedback: [{
                                type: 'SendRequest',
                                identifier: {type: 'OrderCreate'},
                                httpRequest: OrderResource.create(
                                    session.manufacturer.id,
                                    fromSteps(
                                        state.currentTab.step1,
                                        state.currentTab.step2,
                                        state.currentTab.step3,
                                    ),
                                )
                            }]
                        }
                    }
                    case 'Step5Tab': {
                        return {
                            state,
                            feedback: [{
                                type: 'RunEffect',
                                effect:
                                    navigate<OrderCreateAction>(
                                        url({type: 'OrderOverview'}),
                                        {type: 'CreateOrderNoOp'},
                                    ),
                            }],
                        }
                    }
                }
            })()
        }
        case 'OrderCreateResponse': {
            const result = decodeHttpResponse(string(), action.result)
            return {
                state: update(
                    {
                        currentTab:
                            ((): OrderCreateTab => {
                                switch (result.type) {
                                    case 'Ok':
                                        return {
                                            type: 'Step5Tab',
                                            index: 5,
                                            orderNumber: result.value,
                                        }
                                    case 'Err':
                                        return state.currentTab
                                }
                            })(),
                        createdOrder: OptionalWebData.fromResult(result),
                    },
                    state,
                ),
                feedback: [],
            }
        }
        case 'MenuButtonClicked':
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        case 'Step1Action':
            if (state.currentTab.type === 'Step1Tab') {
                const loop = step1Reducer(
                    state.currentTab.state,
                    action.subAction,
                )
                return {
                    state: update(
                        {
                            currentTab: update(
                                {state: loop.state},
                                state.currentTab,
                            ),
                        },
                        state,
                    ),
                    feedback: loop.feedback,
                }
            } else {
                return {state, feedback: []}
            }
        case 'Step2Action':
            if (state.currentTab.type === 'Step2Tab') {
                const loop = step2Reducer(
                    session,
                    state.currentTab.step1.customer.id,
                    state.currentTab.state,
                    action.subAction,
                )
                return {
                    state: update(
                        {
                            currentTab: update(
                                {state: loop.state},
                                state.currentTab,
                            ),
                        },
                        state,
                    ),
                    feedback: loop.feedback,
                }
            } else {
                return {state, feedback: []}
            }
        case 'Step3Action':
            if (state.currentTab.type === 'Step3Tab') {
                return {
                    state: update(
                        {
                            currentTab: update(
                                {
                                    state: step3Reducer(
                                        state.currentTab.state,
                                        action.subAction,
                                    ),
                                },
                                state.currentTab,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            } else {
                return {state, feedback: []}
            }
        case 'CreateOrderNoOp':
            return {state, feedback: []}
    }
}

export function httpResponseAction(
    identifier: OrderCreateRequestIdentifier,
    result: Result.Result<HttpError, string>
): OrderCreateAction {
    switch (identifier.type) {
        case 'CustomerList':
            return {
                type: 'Step1Action',
                subAction: {
                    type: 'ListCustomersResponse',
                    result,
                },
            }
        case 'CompanySuggestionList':
            return {
                type: 'Step1Action',
                subAction: {
                    type: 'CompanySuggestionListResponse',
                    result,
                },
            }
        case 'BpsUserSuggestionList':
            return {
                type: 'Step1Action',
                subAction: {
                    type: 'BpsUserSuggestionListResponse',
                    result,
                },
            }
        case 'ProductSuggestionList':
            return {
                type: 'Step2Action',
                subAction: {
                    type: 'ProductAddProductSuggestionListResponse',
                    result,
                },
            }
        case 'OrderCreate':
            return {
                type: 'OrderCreateResponse',
                result,
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderCreate/OrderCreateReducer.ts